import { type InstructorDistribution } from '../models/Distribution'

/**
 * Maps BE anonymous public survey URLs (`…/surveys/<tenant_token>/<distribution_id>/public/`) to SPA `/public/surveys/…`.
 * BE builds these via `build_public_survey_distribution_url` / `build_distribution_survey_url`; absolute URLs use `{HOST}{API_PREFIX}`.
 */
export function buildPublicSurveyShareUrlFromApi (
  surveyPublicUrl: string | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string | null {
  if (surveyPublicUrl == null || String(surveyPublicUrl).trim() === '') {
    return null
  }
  const raw = String(surveyPublicUrl).trim()

  try {
    const baseForRelative = origin !== '' ? origin : 'http://localhost'
    const parsed = new URL(raw, baseForRelative)
    const pathname = parsed.pathname.replace(/\/+$/, '') || '/'
    const search = parsed.search

    if (pathname.startsWith('/public/surveys')) {
      return `${origin}${pathname}${search}`
    }

    const surveysPublic = pathname.match(/^(.*)\/surveys\/(.+)\/public$/)
    if (surveysPublic === null) {
      return null
    }

    const rest = surveysPublic[2]
    const segments = rest.split('/').filter(Boolean)
    if (segments.length === 1) {
      return `${origin}/public/surveys/${segments[0]}?isPublic=true`
    }
    if (segments.length === 2) {
      return `${origin}/public/surveys/${segments[0]}/${segments[1]}?isPublic=true`
    }
    return null
  } catch {
    return null
  }
}

/**
 * Map BE absolute or relative URLs to the SPA origin; strips a leading `/api` path prefix when present.
 * Used for authenticated survey links and fallbacks when resolving `distribution_url`.
 */
export function normalizeBackendSurveyUrlToAppOrigin (
  raw: string,
  origin: string
): string | null {
  try {
    const u = new URL(raw.trim(), origin)
    let path = u.pathname
    if (path.startsWith('/api/')) {
      path = path.slice(4)
    }
    return `${origin}${path}${u.search}${u.hash}`
  } catch {
    return null
  }
}

/**
 * Path-only target for React Router from student distribution list rows (`distribution_url` may be API-absolute).
 * Same-origin only; unknown/off-origin URLs fall back to `/surveys/:surveyId`.
 */
export function studentSurveyListNavigatePath (
  surveyURL: string | undefined | null,
  surveyIdFallback: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  const trimmed = surveyURL?.trim() ?? ''
  if (trimmed === '') {
    return `/surveys/${surveyIdFallback}`
  }

  const asPublic = buildPublicSurveyShareUrlFromApi(trimmed, origin)
  const atAppOrigin = asPublic ?? normalizeBackendSurveyUrlToAppOrigin(trimmed, origin)
  if (atAppOrigin !== null) {
    try {
      const u = new URL(atAppOrigin)
      if (u.origin === origin) {
        return `${u.pathname}${u.search}${u.hash}`
      }
    } catch {
      /* fall through */
    }
  }

  try {
    const u = new URL(trimmed, origin)
    let path = u.pathname
    if (path.startsWith('/api/')) {
      path = path.slice(4)
    }
    return `${path}${u.search}${u.hash}`
  } catch {
    return `/surveys/${surveyIdFallback}`
  }
}

/**
 * Resolves share/copy targets from `distribution_url` (`distributionUrl`) on instructor/admin distribution list rows.
 * Anonymous → multitenant-safe public SPA URL; identified → authenticated `/surveys/:id` on the app origin (`API_PREFIX` stripped).
 * Returns `null` for an anonymous row without `distributionUrl`: only the Backend has the tenant token, and a
 * tokenless public link fails with 400 "Missing tenant token." (CR-347).
 */
export function resolveInstructorStudentSurveyShareUrl (
  distribution: Pick<InstructorDistribution, 'distributionUrl'>,
  distributionId: string,
  isAnonymous: boolean,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string | null {
  const distUrl = distribution.distributionUrl?.trim() ?? ''

  if (isAnonymous) {
    if (distUrl === '') return null
    return buildPublicSurveyShareUrlFromApi(distUrl, origin) ?? normalizeBackendSurveyUrlToAppOrigin(distUrl, origin)
  }

  if (distUrl !== '') {
    const app = normalizeBackendSurveyUrlToAppOrigin(distUrl, origin)
    if (app !== null) return app
  }

  return `${origin}/surveys/${distributionId}`
}
